/**
 * Layout for /sudo/* — the platform super-admin surface.
 *
 * Visible only to emails on the SUPER_ADMIN_EMAILS allowlist. Server-side
 * gating happens inside /api/super/*; this layout adds a UI gate so an
 * unauthorised visitor sees a clear 403 instead of a half-loading page.
 *
 * The dark band at the top makes the super-admin surface visually
 * distinct from the per-business admin (which uses the cream chrome).
 */
import { Link, Outlet, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Shield } from 'lucide-react';
import { AppHeader } from '../components/AppHeader';
import { authClient } from '../auth/client';

interface MeSuperAdmin {
  ok: boolean;
}

export const Route = createFileRoute('/sudo')({
  component: SudoLayout,
});

async function probeAccess(): Promise<MeSuperAdmin> {
  // The cheapest authorised endpoint — overview is small and cached
  // server-side wouldn't hurt later. For now, we just probe.
  const res = await fetch('/api/super/overview', {
    headers: { accept: 'application/json' },
  });
  return { ok: res.ok };
}

function SudoLayout() {
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const access = useQuery({
    enabled: Boolean(session?.user),
    queryKey: ['sudo', 'access'],
    queryFn: probeAccess,
    staleTime: 60_000,
  });

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <SudoBand />

      <main className="mx-auto max-w-6xl px-6 py-10 sm:px-12">
        {sessionPending ? (
          <p className="text-sm text-muted-foreground">Checking access&hellip;</p>
        ) : !session?.user ? (
          <NotSignedIn />
        ) : access.isLoading ? (
          <p className="text-sm text-muted-foreground">Checking access&hellip;</p>
        ) : access.data?.ok ? (
          <Outlet />
        ) : (
          <Forbidden email={session.user.email} />
        )}
      </main>
    </div>
  );
}

function SudoBand() {
  // Background poll for unread count. Key sits under ['sudo',
  // 'conversations', ...] so the reply / status-change invalidations
  // in /sudo/inbox/$id naturally drop the badge instantly; otherwise
  // refetch every 30s as a safety net.
  //
  // The trailing `unread-count` segment is deliberate: the inbox list
  // page uses ['sudo', 'conversations', 'unread'] as the queryKey for
  // its `Unread` filter pill, which returns the full list. Sharing
  // that exact key was a bug — React Query would serve whichever
  // populated first and the other consumer would read the wrong
  // shape, throwing "Cannot read properties of undefined".
  const unread = useQuery({
    queryKey: ['sudo', 'conversations', 'unread-count'],
    queryFn: async () => {
      const res = await fetch('/api/super/conversations?status=new');
      if (!res.ok) throw new Error('list_failed');
      const body = (await res.json()) as { conversations: unknown[] };
      return body.conversations.length;
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const unreadCount = unread.data ?? 0;

  return (
    <div className="border-b border-brand-ink bg-brand-ink text-brand-cream">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-2.5 sm:px-12">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4" aria-hidden />
          <span className="text-xs font-semibold uppercase tracking-widest">
            Super-admin
          </span>
        </div>
        <nav className="flex items-center gap-1 text-xs">
          <Link
            to="/sudo"
            activeOptions={{ exact: true }}
            className="rounded-md px-2.5 py-1 text-brand-cream/70 transition-colors hover:text-brand-cream"
            activeProps={{ className: 'bg-brand-cream/10 text-brand-cream' }}
          >
            Overview
          </Link>
          <Link
            to="/sudo/inbox"
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-brand-cream/70 transition-colors hover:text-brand-cream"
            activeProps={{ className: 'bg-brand-cream/10 text-brand-cream' }}
          >
            Inbox
            {unreadCount > 0 ? (
              <span
                aria-label={`${unreadCount} unread`}
                className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-rose-600 px-1.5 text-[10px] font-bold leading-none text-white tabular-nums"
                style={{ height: '1.25rem' }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            ) : null}
          </Link>
          <Link
            to="/sudo/templates"
            className="rounded-md px-2.5 py-1 text-brand-cream/70 transition-colors hover:text-brand-cream"
            activeProps={{ className: 'bg-brand-cream/10 text-brand-cream' }}
          >
            Templates
          </Link>
          <Link
            to="/sudo/api-usage"
            className="rounded-md px-2.5 py-1 text-brand-cream/70 transition-colors hover:text-brand-cream"
            activeProps={{ className: 'bg-brand-cream/10 text-brand-cream' }}
          >
            API usage
          </Link>
        </nav>
      </div>
    </div>
  );
}

function NotSignedIn() {
  return (
    <div className="mx-auto max-w-md rounded-2xl border border-brand-sage/15 bg-card p-8 text-center shadow-brand">
      <h1 className="text-2xl font-bold tracking-tight">Sign in required</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        The super-admin surface needs a session.
      </p>
      <Link
        to="/signin"
        className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Go to sign-in
      </Link>
    </div>
  );
}

function Forbidden({ email }: { email: string }) {
  return (
    <div className="mx-auto max-w-md rounded-2xl border border-brand-sage/15 bg-card p-8 text-center shadow-brand">
      <h1 className="text-2xl font-bold tracking-tight">Not allowed</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        <span className="font-mono">{email}</span> isn&rsquo;t on the
        super-admin allowlist. If that&rsquo;s wrong, update{' '}
        <code className="rounded bg-muted px-1 py-0.5">SUPER_ADMIN_EMAILS</code>{' '}
        on the Worker.
      </p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center justify-center rounded-md border border-border px-5 py-2.5 text-sm font-medium hover:bg-secondary"
      >
        Back to app
      </Link>
    </div>
  );
}
